export interface Update {
  slug: string
  version: string
  title: string
  date: string
  description: string
  content: string
  videoId?: string
  blogUrl?: string
}

export const updates: Update[] = [
  {
    slug: 'v0-0-22-insight-memory-and-projects',
    version: 'v0.0.22',
    title: 'Capture customer feedback and turn it into tracked Projects',
    date: 'September 2026',
    description:
      'Paste call snippets, reviews, or messages into chat to save them in Insight Memory. Search for related feedback, then create a strategy-grounded Project from a cluster or link insights to work you already track.',
    content: `
Scattered feedback stops being something you re-find every quarter. Paste a call snippet, review, or Slack export into the sidepanel and ask SupaPM to capture it — each piece lands in Insight Memory on your machine with an embedding so you can search and relate it later. When you are ready to act, ask what else sounds similar, then turn that cluster into a real Project (not a throwaway note): title, description, strategic rationale pulled from your org knowledge Strategy section, and explicit links back to the source insights. Or link one insight to a project you already have.

Insight-to-insight relatedness is computed when you ask (search_insights), not maintained as a cluttered link graph on the browsing page. Insight-to-project links are persisted only when you or the agent attach them on a direct ask — never silently from similarity alone.

## What's new

- \`capture_insight\` in chat: extract and save feedback locally (OpenAI or Google key required for embeddings).
- Options → Insight Memory: browse, search, and delete captured insights; see which projects each insight is linked to.
- \`search_insights\`: keyword search plus live related-insight scores for clustering before you commit to a project.
- \`create_project_from_insights\` and \`link_insight_to_project\`: roll signal into tracked work with Customer insights on the project in the sidepanel.
- Coming next (roadmap): suggested project links with confirm, and strategy-weighted digests for noisy themes — see the Insights → Projects PRD.
`.trim(),
  },
  {
    slug: 'v0-0-21-conversation-cost-and-classifier',
    version: 'v0.0.21',
    title: 'See what a conversation costs, and speed up chat with an AI Classifier',
    date: 'September 2026',
    description:
      'Chat now shows a running $ estimate for the conversation, priced automatically from each provider\'s published rates. An optional AI Classifier key can also route messages to only the tools they need, cutting cost and latency on simple turns.',
    content: `
Chat now shows what it costs. A subtle running total sits under the model selector and in the context-usage popover, so you can see the $ cost of a conversation at a glance instead of guessing from token counts. It's priced automatically for OpenAI, Anthropic, and Google models using each provider's own published rates — no setup required.

You can also add an optional AI Classifier key to speed up chat. Before calling your main model, SupaPM asks a fast, cheap routing model which category of tools a message likely needs, and only sends that category instead of every tool every time. It's a bring-your-own-key connector with the same local-only privacy model as your other provider keys, and it's entirely optional: without a key, chat behaves exactly as it always has.

## What's new

- Sidepanel chat shows a running "Conversation cost" estimate under the model selector and in the context-usage popover, covering the whole chat, not just the latest message.
- Cost is estimated automatically for every current OpenAI, Anthropic, and Google model, using each provider's published per-token rates, including the discount for cached input and the extra charge for cache writes.
- Settings → AI Configuration lets you see and override the price for any model, right where you manage that provider's API key.
- Settings → AI Configuration has a "Test key" button for every provider (including Custom endpoints) that makes a live check to confirm a key actually works before you rely on it.
- New optional AI Classifier connector: add a Jev API key in Settings to let SupaPM route each message to only the tools it likely needs, reducing cost and latency on turns that don't need everything. Skip it and nothing changes.
`.trim(),
  },
  {
    slug: 'v0-0-20-pdf-attachments',
    version: 'v0.0.20',
    title: 'Attach a PDF to chat',
    date: 'September 2026',
    description:
      'Attach a PDF to sidepanel chat the same way you already attach an image or a text file, and ask the assistant to summarize it, pull out terms, or draft a reply.',
    content: `
You can now attach a PDF to chat, the same way you already attach an image or a text file. Drop in a signed contract, a security questionnaire, a vendor's PDF one-pager, or a support ticket exported to PDF, and ask the assistant to summarize it, pull out specific terms, or draft a reply, without converting it to text yourself first.

The PDF is sent to your configured AI provider as-is. OpenAI, Anthropic, and Google all read PDFs directly, so there is no separate conversion step on our side, and the file still only reaches the provider you have configured for that chat.

## What's new

- Attach a PDF with the paperclip button or by pasting a file into the chat input, same as any other attachment.
- The PDF is sent to your configured AI provider as-is, so there is no separate conversion step on our side.
- PDFs up to 15MB are supported, a higher limit than other attachments since documents like contracts and reports tend to run longer.
`.trim(),
  },
  {
    slug: 'v0-0-19-multi-provider-connections',
    version: 'v0.0.19',
    title: 'Keep multiple AI providers connected at once',
    date: 'September 2026',
    description:
      'Save a custom or local model endpoint alongside OpenAI, Anthropic, and Google without losing the others, and captured page or screenshot context now stays attached across follow-up questions.',
    content: `
You can keep a custom or local model connected at the same time as OpenAI, Anthropic, or Google. Saving a custom endpoint no longer hides your other keys, so you can use Claude for a stakeholder draft and a local model for a sensitive customer transcript in the same session.

Captured context is also easier to trust across a longer conversation. Context you capture from a page, a screenshot, or a text selection now stays attached to the chat across follow-up questions, instead of being cleared the moment you send the message it was captured for.

## What's new

- Custom OpenAI-compatible endpoints now appear in the model switcher as another provider, alongside catalog providers you have keys for.
- Saving a catalog key or a custom endpoint still selects that connection, but the others stay available to switch to.
- Onboarding tests every connection you have added, not only the last one saved.
- Captured page, screenshot, or selection context stays attached across follow-up questions instead of clearing after the message it was captured for. Ask a first question about a captured roadmap doc, then a follow-up a few messages later, and the assistant still has it. Remove a context chip yourself when you are done with it.
`.trim(),
  },
  {
    slug: 'v0-0-18-read-granted-tabs',
    version: 'v0.0.18',
    title: 'The assistant can read allowed pages as part of the work',
    date: 'September 2026',
    description:
      'SupaPM can now pull in a Confluence runbook or Jira ticket on its own when you ask — you no longer have to attach each page by hand. It only touches sites you have allowed.',
    content: `
What changed is autonomy. When you ask what is blocking a project, SupaPM can open or reuse the Confluence runbook and the Jira tickets, read them, and answer from that context in the same turn. Before this release you had to add each page yourself with @page, which added friction and slowed the workflow down.

The extra reach is still gated the same way highlighted text is. Only sites you have allowed for page context are in scope. If you have not granted Confluence, those tabs are invisible and SupaPM does not call Confluence at all. Other tabs in the window stay private. The assistant still cannot click, type, or submit forms.

## What's new

- The assistant can read an allowed page as a step in a query, not only when you attach it.
- Already-open tabs are reused; a missing page can be opened and read without you leaving chat.
- Chat shows which page was opened or read, so you can see what was included.
`.trim(),
  },
  {
    slug: 'v0-0-17-local-folders',
    version: 'v0.0.17',
    title: 'Ask the codebase without pasting the repo',
    date: 'September 2026',
    description:
      'Grant a local folder so sidepanel chat can search and read the files that answer a product question, without dumping a whole codebase into context.',
    content: `
You can now point SupaPM at a local folder — a product codebase, a dump of interview notes, or a research directory — and ask questions against it from the side panel. The agent does not swallow the whole tree. It gets a short map, then lists, searches, and reads only the slices it needs, the same way a coding assistant explores a repo.

Connect folders in Options, then attach one to a chat with @ and the folder name. They are not added to every conversation. Access is read-only: SupaPM will not write to your disk. Chrome and Edge grant a live folder handle and read files on demand. This is not offered in Firefox — Firefox has no live folder-handle API.

Secret-like files are refused, and common generated folders are ignored. When the agent reads a file, those bytes go to your configured AI provider, the same as attachments today — not to SupaPM servers.

## What's new

- Options → Local folders: grant a folder, rescan, or remove access.
- Attach a granted folder to the current chat with @. The side panel shows attached folders and prompts to allow access when the browser has dropped permission.
- Agent tools list, search, and read local files with hard caps so a naive search cannot pull a monorepo into context.
- Default ignore list plus \`.gitignore\`, with a hard refusal for \`.env\`, keys, and credential filenames.
`.trim(),
  },
  {
    slug: 'v0-0-16-clearer-chat-and-current-models',
    version: 'v0.0.16',
    title: 'Clearer chat, current models, and steadier sidepanel work',
    date: 'September 2026',
    description:
      'Chat is easier to trust when something goes wrong, models stay current without a cluttered picker, and day-to-day sidepanel work feels steadier.',
    content: `
This release makes day-to-day sidepanel work feel steadier. Provider failures such as running out of credits now show a clear explanation instead of a generic stream error. Thinking models surface their steps as collapsible headings while they work. Chats no longer bleed into each other if you switch tabs mid-reply. The model list is trimmed to current options, and if a saved model has been sunset SupaPM switches you to its successor and asks you to confirm.

You can also see token usage per reply and for the whole session, and check how much browser storage SupaPM is using in Settings.

## What's new

- Provider errors unpack billing, API key, rate-limit, and context-window failures into actionable messages, with an Open settings shortcut when relevant.
- Failed replies appear as error cards and stay out of the next model request so they do not pollute conversation context.
- Thinking-model reasoning streams as collapsible step headings; the active step shimmers until the next one arrives.
- Per-turn and session token usage is visible in chat, including thinking tokens when the provider reports them.
- Model picker shows current-generation options only (for example GPT-5.6 Sol / Terra / Luna). Sunset models remap to a successor with a confirmation dialog.
- Sidepanel toasts use clearer coloured alerts with icons and a dismiss control.
- Slash shortcuts only open when \`/\` stands alone (for example after a space), so mid-word typing does not pop the prompt menu.
- Chats stay isolated while generating: switch tabs or start a new chat without losing an in-flight reply.
- Settings shows local storage usage for SupaPM data in this browser profile.
`.trim(),
  },
  {
    slug: 'v0-0-15-reliable-agent-document-edits',
    version: 'v0.0.15',
    title: 'More reliable agent document edits',
    date: 'June 2026',
    description:
      'The agent can update Company Context, skills, and project descriptions without spurious find-and-replace failures when whitespace is normalised.',
    content: `
Targeted document edits are more dependable in day-to-day PM work. When the agent captures a passage to change in Company Context, a skill, or a project description, minor whitespace differences — such as a collapsed blank line or normalised indentation — no longer cause the edit to fail with a "not found" error.

## What's new

- \`edit_document\` find-and-replace tolerates minor whitespace differences so the agent can apply targeted updates without spurious match failures.
`.trim(),
  },
  {
    slug: 'v0-0-14-trustworthy-context-capture-and-faster-sidepanel',
    version: 'v0.0.14',
    title: 'Trustworthy context capture and a faster sidepanel',
    date: 'May 2026',
    description:
      'Captured page context now preserves links and behaves like attachments you can inspect before sending, and the sidepanel opens immediately without waiting on the model catalog.',
    content: `
This release fixes a crash loop that made the sidepanel unresponsive, ensures hyperlinks are preserved when you capture page context, and makes captured context much easier to understand and trust. Page, screen, and selection context now behaves like file attachments: it sits in the same staging area before you send, clears when the message goes, and shows up in your message history. Any context or attachment chip is clickable to inspect exactly what text was sent to the model — useful when page extraction produces noise or you want to verify a capture before a sensitive query. The sidepanel also opens noticeably faster: the AI model list is now served from a local cache on every reopen, so the panel is ready to use without waiting on a network round-trip.

## What's new

- Styling updates: Updated the font to use the same as the main website, improved tab management
- Sidepanel no longer blocks on a network fetch when opening. The AI model catalog is persisted to local storage after the first load and served from there on every subsequent open.
- Updated the model catalog with Gemini 3.5 Flash, Gemini 3.1 Flash-Lite, and GPT-5.4 nano. Deprecated the preview aliases those stable models replace, and fixed the OpenAI default model from the deprecated GPT-4o mini to GPT-5.4 mini.
- Fixed an infinite injection loop that could make the sidepanel unresponsive on certain pages.
- @selection captures now preserve links, headings, bold/italic, code, and list structure as markdown.
- @page and @screen captures now emit links as markdown instead of dropping them.
- JIRA ticket descriptions and Slack message content are converted from HTML to markdown so links and formatting are preserved.
- Context chips are shown in the same pre-send staging area as file and image attachments, and are consumed per message rather than persisting across turns.
- Clicking any chip opens a content inspector showing the full text, source URL, character count, capture time, and a Copy button.
`.trim(),
  },
  {
    slug: 'v0-0-13-company-context-and-chat-workflow-upgrade',
    version: 'v0.0.13',
    title: 'Company Context upgrade, richer chat inputs, and cleaner edit cards',
    date: 'May 2026',
    description:
      'Company Context replaces Org Knowledge, chat now supports file/image attachments and stop control, and edit events are easier to scan by meaning.',
    content: `
This release makes day-to-day PM workflows in the extension more practical and easier to trust. Org Knowledge is now Company Context across the product, the default context document is more structured, and the assistant has clearer routing hints for what context to load.

Chat is also more usable for real PM artifacts. You can attach files from disk, paste screenshots directly into chat, and stop a generation when it has already produced enough. The edit-event UI was simplified so the card header emphasizes what changed instead of repeating obvious metadata.

## What's new

- **Company Context naming and structure**: Org Knowledge was renamed throughout the extension, and the default context template now includes clearer business sections plus personal sections.
- **Markdown editor control**: Added a Raw/Styled toggle so you can switch between rich editing and raw markdown in system prompt, skills, and project description surfaces.
- **Attachment-aware chat**: Attach text/code files or paste images into chat, with model-safe request formatting for each attachment type.
- **Stop in-flight generation**: Interrupt responses when they are complete enough and keep useful partial output.
- **Edit-event scanability**: Chat edit cards now focus header content on document + summary, with deeper diff metadata available on expansion.
`.trim(),
  },
  {
    slug: 'v0-0-12-context-memory-and-sidepanel-navigation-polish',
    version: 'v0.0.12',
    title: 'Context memory and sidepanel navigation polish',
    date: 'May 2026',
    description:
      'Better trust and continuity in chat with clearer turn inspection, stronger loaded-context memory, and smoother sidepanel navigation.',
    content: `
This release improves how confidently PMs can rely on SupaPM during multi-step work. Loaded Skills and Org Knowledge are now carried forward more intentionally between turns, and debug mode makes it easier to inspect exactly what context was sent to the model when a response looks off.

It also sharpens day-to-day sidepanel flow: options quick navigation is easier to reach, dropdown behavior now uses DaisyUI defaults for more consistent interactions, and automatic focus jumps were removed to reduce accidental project picks.

## What's new

- Added preflight debug payload capture so Inspect Turn can show the assembled system prompt and outbound messages before each model call.
- Improved carry-forward memory for prior tool outcomes, including focused memory formatting for loaded Skills and Org Knowledge.
- Preserved canonical loaded-context markdown with freshness checks so loaded context stays available and version-aware across turns.
- Improved context-window usage reporting to avoid double counting cumulative chat tokens.
- Added options quick-nav links in the sidepanel header for faster jumps to Chats, Skills, Prompts, Org Knowledge, Edit History, and Settings.
- Switched sidepanel options quick-nav to DaisyUI native dropdown behavior for more predictable open/close interactions and fewer custom state edge cases.
- Removed sidepanel auto-focus jumps that could briefly reopen project picker suggestions and cause accidental selection.
- Expanded local project search tool filters and sorting to improve project triage workflows in chat.
`.trim(),
  },
  {
    slug: 'v0-0-11-reasoning-controls-and-shortcut-based-context-capture',
    version: 'v0.0.11',
    title: 'Reasoning controls and shortcut-based context capture',
    date: 'May 2026',
    description:
      'Tune assistant reasoning depth in chat and add highlighted text to context explicitly with a keyboard shortcut.',
    content: `
This release gives PMs more control over both model reasoning and page context capture. You can now choose reasoning depth directly in the side panel model picker for harder tasks, and add highlighted text to context with a deliberate keyboard shortcut instead of relying on focus-based behavior.

The result is a more predictable workflow when you are collecting quotes from customer calls, competitor pages, docs, and internal notes: capture only what you want, when you want it, and control how deeply the model should think before responding.

## What's new

- Reasoning depth controls in the side panel model popup with low, medium, and high options.
- Model catalog capability flags (\`supports_thinking\`) so reasoning controls only appear for compatible models.
- Provider-specific reasoning settings for OpenAI, Anthropic, and Google so selected depth is applied to runtime calls.
- Improved Gemini reasoning behavior by requesting thought content (\`includeThoughts\`) so supported reasoning output can appear in the UI.
- Persistent reasoning-level preference so your selected depth carries across sessions.
- Native highlighted-text shortcut capture (\`Ctrl+Shift+Y\` / \`Command+Shift+Y\`) in Chrome and Firefox.
- Focus-based selected-text auto-attach removed in favor of explicit shortcut-triggered capture.
- Clearer in-panel capture feedback and duplicate-selection protection for repeated \`url + exact text\` captures.
`.trim(),
  },
  {
    slug: 'v0-0-10-security-transparency-and-release-marketing-checks',
    version: 'v0.0.10',
    title: 'Turn highlighted page text into useful chat context',
    date: 'May 2026',
    description:
      'Highlight text on a page, focus the SupaPM side panel, and use that exact customer, competitor, or project detail as chat context.',
    content: `
This release makes page context more useful in the moment PMs actually need it. When you highlight text on a page and focus the SupaPM side panel, that selection becomes a context chip for your next message.

That means you can pull a quote from a customer email, a pricing line from a competitor page, a confusing Jira note, or a requirement from a product doc straight into the assistant without copying, pasting, or re-explaining where it came from.

Because this feature reads highlighted page text, it is explicit. SupaPM asks before enabling text capture for a site. You can allow the current site, allow all sites, or manage/remove granted sites from Settings.

## What's new

- **Highlighted text as context**: Select text from a page, focus the side panel chat input, and SupaPM adds it as a context chip.
- **Faster PM workflows**: Ask SupaPM to summarise a customer quote, compare a competitor claim, turn a Jira comment into a status update, or draft a follow-up from selected text.
- **No webpage overlay**: The old floating "Add as context" button is gone, so pages stay clean.
- **Explicit site permissions**: Enable highlighted-text capture for one site or all sites, with the ability to remove access later in Settings.
- **Security transparency**: SupaPM now has a public security page and extension SBOM for teams that need to review the browser extension before rollout.

The goal is simple: make the useful bit of the page follow you into the chat, while keeping page access visible and under your control.
`.trim(),
  },
  {
    slug: 'v0-0-9-model-catalog-and-multi-provider-ai-settings',
    version: 'v0.0.9',
    title: 'Model catalog, multi-provider AI settings, and better chat controls',
    date: 'May 2026',
    description:
      'SupaPM now discovers supported models from a versioned catalog, lets you save API keys for multiple providers, and gives the side panel a richer model switcher.',
    content: `
This release makes model setup much more flexible. Instead of locking yourself into one provider, you can now save API keys for OpenAI, Anthropic, and Google independently, then switch models from the side panel when the task calls for it.

The extension now uses SupaPM's public model catalog to discover supported providers and models. That means newer models can appear without hardcoding every option into the extension UI, while unavailable or incompatible models are filtered out automatically.

## What's new

- **Multi-provider API keys**: Save keys for more than one provider and manage them from Settings or onboarding.
- **Model catalog**: Supported models now come from a versioned SupaPM catalog, with compatibility checks built in.
- **New model options**: Added Claude Opus 4.7, Claude Sonnet 4.6, Claude Haiku 4.5, Gemini 3.1 Pro Preview, Gemini 3 Flash Preview, and Gemini 3.1 Flash-Lite Preview.
- **Side panel model picker**: Switch between configured providers and models from chat, with short descriptions and context-window metadata.
- **Manual endpoints**: Advanced users can point SupaPM at an OpenAI-compatible local or custom-hosted model endpoint.
- **Better chat transparency**: Token/context usage metadata and explicit reasoning blocks are now surfaced more clearly when available.

The goal is simple: keep SupaPM local-first and provider-flexible without making model setup feel like an admin chore.
`.trim(),
  },
  {
    slug: 'v0-0-8-skills-pages-chat-polish-and-markdown-editor-updates',
    version: 'v0.0.8',
    title: 'Dedicated Skills pages, smoother chat, and richer markdown editing',
    date: 'April 2026',
    description:
      'A workflow polish release focused on making Skills easier to manage, chat smoother to use, and markdown editing more capable.',
    content: `
This update improves the day-to-day editing and chat workflow inside SupaPM. Skills now behave more like first-class documents, with list and detail pages instead of modal editing, and the chat composer sits inside the chat surface where it belongs.

## What's new

- **Dedicated Skills pages**: Skills now use index and detail routes, making them easier to scan, open, create, and edit.
- **Markdown-style Skills editing**: Skill instructions use the same richer markdown rendering approach as project and org knowledge documents.
- **More cohesive side panel chat**: The composer is part of the chat card, keeping the conversation and input together.
- **Smoother streaming**: Streaming follows the bottom of the conversation only when you are already there, so reading older messages is less frustrating.
- **Less abrupt scrolling**: Removed side panel scroll snapping for a calmer stacked layout.
- **Markdown editor improvements**: Centralized markdown styles, refined table/code/blockquote rendering, and added Notion-style date mentions such as \`@today\` and \`@next monday\`.

This release is mostly about reducing friction for people who use SupaPM every day.
`.trim(),
  },
  {
    slug: 'v0-0-7-agent-editable-documents-and-safer-project-context',
    version: 'v0.0.7',
    title: 'Agent-editable documents and safer project context',
    date: 'April 2026',
    description:
      'The assistant can now edit local reference documents with history, while project selection in the side panel is less disruptive.',
    content: `
This release makes SupaPM more useful as a working assistant, not just a chat box beside your browser.

The biggest change is that the agent can now edit key local documents: Org Knowledge, Skills, and the selected project's description. Those edits are tracked with history, diffs, restore actions, and concurrency checks so an AI edit is visible and reversible instead of silently changing your source of truth.

## What's new

- **Agent-editable documents**: The chat agent can update Org Knowledge, Skills, and selected project descriptions.
- **Edit history**: A new Options page lists document edit events, grouped by document, with filters and restore support.
- **Diffs in chat**: Agent edits appear inline as visible edit events.
- **Safer project selection**: Page URL matches are suggestions only. SupaPM no longer auto-switches your selected project when you bounce between tabs.
- **Suggested project matches**: The project search bar surfaces relevant projects for the current page without changing your active context.
- **Persisted selected project**: The side panel remembers your last selected project across close and reopen.
- **Projects refactor**: Project list, board, and search views are brought together into one Projects page.
- **Org Knowledge template**: The default org knowledge boilerplate now gives teams a better starting point for strategy, ICP, pricing, competitors, and OKRs.

The theme of this release is control: the agent can help more directly, but important context changes remain explicit, visible, and reversible.
`.trim(),
  },
  {
    slug: 'v0-0-6-side-panel-layout-project-search-and-markdown-tables',
    version: 'v0.0.6',
    title: 'Better side panel layout, project search, and markdown tables',
    date: 'March 2026',
    description:
      'Side panel layout improvements, better project search, favicon support for links, and markdown table support for project documents.',
    content: `
This release tightens the core side panel experience. SupaPM now makes better use of wider browser side panels, project selection is easier to reach, and project documents can include tables.

## What's new

- **Two-column side panel mode**: When the side panel is wide enough, project details and chat sit side by side.
- **Improved project search**: The project combobox now lives in the header, giving the main project and chat surfaces more room.
- **Cleaner project details**: Project sections use collapsible panels with better scrolling and padding behavior.
- **Favicons for project links**: Links in projects are easier to scan at a glance.
- **Markdown tables**: Project documents now support tables for more structured notes.
- **Copyable code blocks**: Assistant messages with fenced code blocks render each block with a dedicated copy action.
- **Theme icon fixes**: Icons display more reliably in both light and dark mode.

It is not a flashy release, but it makes the extension feel more solid in the places PMs spend the most time.
`.trim(),
  },
  {
    slug: 'v0-0-5-skills-and-model-picker-fixes',
    version: 'v0.0.5',
    title: 'Skills and model picker fixes',
    date: 'March 2026',
    description:
      'Added local Skills for repeatable workflows and fixed which models appear as available in the AI picker.',
    content: `
This release introduces Skills: local markdown workflows that help SupaPM follow your preferred ways of working.

Skills are stored locally and included in AI context as metadata. When the assistant decides a skill is relevant, it can load the full instructions and apply them to the task.

## What's new

- **Skills**: Create local markdown workflows for repeatable product-management tasks.
- **Better AI model picker**: The model picker now more accurately reflects what each provider exposes and supports.

This is the first step toward making SupaPM feel less like a generic chat assistant and more like a teammate that knows your operating style.
`.trim(),
  },
  {
    slug: 'v0-0-4-browser-project-management-ui-improvements',
    version: 'v0.0.4',
    title: 'Minor UI improvements for in-browser project management',
    date: 'March 2026',
    description:
      'A focused quality-of-life update to make project management in the browser faster, cleaner, and easier to navigate.',
    videoId: 'Qdsv-U6MLiw',
    content: `
So I tried using SupaPM for my project of making an open source vacuum cleaner (don't ask). I realised immediately that the UI was not ideal for project management. I found myself constantly wanting to update the project itself right from the side panel. So, I've shifted the chat input to the bottom of the side panel, giving the actual project management UI more space.

We still have that markdown-Notion like editing experience, which I really like. 

## What else?

- Improved onboarding flow - no need to capture the user's name, just set up your AI provider and model.
- Homepage of main site update - removed all the fluff
- All policies created regarding cookies, terms and conditions, privacy policy, etc.

So, not a major update, but a focused quality-of-life update to make project management in the browser faster, cleaner, and easier to navigate.
`.trim(),
  },
  {
    slug: 'v0-0-3-initial-launch',
    version: 'v0.0.3',
    title: 'Initial launch – Cursor for Product Managers',
    date: 'February 2026',
    description:
      'SupaPM is now available as a browser extension for Chrome and Firefox. Your AI product management assistant in a side panel—local-first, no heavy integrations required.',
    videoId: 'TrGyYDsv2gM',
    content: `
We've launched SupaPM as a browser extension—a side panel flow inspired by Cursor for development. Your AI product management assistant is now available for [Chrome](https://chromewebstore.google.com/detail/supapm/pjopbojlonaimbfcomcnbadlagcldbgp) and [Firefox](https://addons.mozilla.org/en-US/firefox/addon/supapm/).

## Why a browser extension?

- **Local-first** – Data stays in your browser (except LLM calls). Lower barrier for product teams to try it.
- **No heavy integrations** – Stay open in a side panel, add page context when you need it. No Gmail/Jira setup required to get started.

## Features in this release

- **Organisation & context switching** – Projects as a loose definition of anything that needs work. Keep links, add them via chat, and have project info at hand when you land on a relevant page.
- **Entirely local** – Data stays in your browser (except LLM calls). No proprietary data sent to external servers.
- **Configurable LLM** – Pick OpenAI, Google Gemini, or Anthropic. Enter your own API key.
- **Basic project management** – Create projects, change status, add updates and links via chat. All managed locally.
- **Configurable system prompt** – Customise SupaPM's behaviour to match how your company works.
- **Custom commands** – Shortcuts for repeated actions (e.g. add link to project).
- **Custom project statuses** – Configure statuses that fit your workflow.
- **Light/dark mode** – Theme support.

## On the roadmap

- General UI improvements – colours and text size in the side panel
- Improved onboarding flow
- Context management – summarising chats and continuing
- Local mode for the LLM
`.trim(),
  },
]

export function getUpdateBySlug(slug: string): Update | undefined {
  return updates.find((u) => u.slug === slug)
}

/** Per-update routes for nitro.prerender.routes (add '/updates' separately) */
export const updatePrerenderRoutes = updates.map((u) => `/updates/${u.slug}`)